import React from 'react';
import { Box, Button, MenuItem, Paper, TextField, Typography } from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { getRequest, patchRequest } from 'Includes/api/apicall';
import { GET_URL } from 'Includes/urls';
import Swal from 'sweetalert2';

function SprintPlanning(props) {
  const [rows, setRows] = React.useState([]);
  const [sprintRows, setSprintRows] = React.useState([]);
  const [taskRows, setTaskRows] = React.useState([]);
  const [loading, setLoading] = React.useState(false);
  const [mapping, setMapping] = React.useState({ task_id: '', sprint_id: '' });

  const loadData = React.useCallback(() => {
    setLoading(true);
    getRequest(GET_URL.tasks.api, { sprint_planning: 1 }, props).then((response) => {
      if (response && response.status === 200) {
        const payload = response.data?.data || [];
        const flatRows = [];
        payload.forEach((dayRow) => {
          (dayRow.tasks || []).forEach((task) => {
            flatRows.push({
              id: `${dayRow.date}-${task.id}`,
              date: dayRow.date,
              sprint_name: task.sprint_name,
              title: task.title,
              stage: task.task_stage_name || '',
              priority: task.priority,
              assigned_to_name: task.assigned_to_name,
              deadline: task.deadline,
            });
          });
        });
        setRows(flatRows);
      }
      setLoading(false);
    }).catch(() => setLoading(false));
    getRequest(GET_URL.sprints.api, { limit: 200, pageno: 1 }, props).then((response) => {
      if (response && response.status === 200) {
        setSprintRows(response.data?.data || []);
      }
    });
    getRequest(GET_URL.tasks.api, { limit: 500, pageno: 1 }, props).then((response) => {
      if (response && response.status === 200) {
        const taskData = response.data?.data || [];
        setTaskRows(taskData.filter((task) => task.assigned_to && !task.sprint));
      }
    });
  }, [props]);

  React.useEffect(() => {
    loadData();
  }, [loadData]);

  const mapTaskToSprint = () => {
    if (!mapping.task_id || !mapping.sprint_id) {
      Swal.fire({ icon: 'warning', title: 'Select assigned task and sprint' });
      return;
    }
    patchRequest(`${GET_URL.tasks.api}${mapping.task_id}/`, { sprint: mapping.sprint_id }, props).then(() => {
      Swal.fire({ icon: 'success', title: 'Task mapped to sprint', timer: 1200, showConfirmButton: false });
      setMapping({ task_id: '', sprint_id: '' });
      loadData();
    });
  };

  const columns = [
    { field: 'date', headerName: 'Day', minWidth: 140, flex: 0.7 },
    { field: 'sprint_name', headerName: 'Sprint', minWidth: 160, flex: 0.8 },
    { field: 'title', headerName: 'Task', minWidth: 220, flex: 1.2 },
    { field: 'stage', headerName: 'Stage', minWidth: 120, flex: 0.6 },
    { field: 'priority', headerName: 'Priority', minWidth: 110, flex: 0.5 },
    { field: 'assigned_to_name', headerName: 'Assigned To', minWidth: 160, flex: 0.8 },
    { field: 'deadline', headerName: 'Deadline', minWidth: 130, flex: 0.6 },
  ];

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h5" fontWeight={700}>Sprint Planning</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Map already assigned tasks to sprint.
      </Typography>
      <Paper sx={{ p: 2, borderRadius: 2, mb: 2 }}>
        <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1 }}>Map Assigned Task To Sprint</Typography>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr auto' }, gap: 2 }}>
          <TextField
            fullWidth
            select
            size="small"
            label="Assigned Task"
            value={mapping.task_id}
            onChange={(e) => setMapping((prev) => ({ ...prev, task_id: e.target.value }))}
          >
            {taskRows.map((task) => (
              <MenuItem key={task.id} value={task.id}>
                {`${task.title} - ${task.assigned_to_name}`}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            fullWidth
            select
            size="small"
            label="Sprint"
            value={mapping.sprint_id}
            onChange={(e) => setMapping((prev) => ({ ...prev, sprint_id: e.target.value }))}
          >
            {sprintRows.map((sprint) => (
              <MenuItem key={sprint.id} value={sprint.id}>
                {`${sprint.name} (${sprint.from_date} to ${sprint.to_date})`}
              </MenuItem>
            ))}
          </TextField>
          <Button variant="contained" onClick={mapTaskToSprint}>Map</Button>
        </Box>
      </Paper>
      <Paper sx={{ p: 2, borderRadius: 2 }}>
        <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1 }}>Day Wise Task Assignment</Typography>
        <DataGrid
          autoHeight
          rows={rows}
          columns={columns}
          loading={loading}
          pagination
          pageSizeOptions={[10, 20, 50]}
          initialState={{ pagination: { paginationModel: { page: 0, pageSize: 10 } } }}
          disableRowSelectionOnClick
        />
      </Paper>
    </Box>
  );
}

export default SprintPlanning;

